import React, { ComponentType, createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import checkFeatureFlag from 'shared/utils/featureFlagUtil';
import { ACCOUNT_FEATURES } from 'users/constants/usersConstants';
import { useRootStore } from 'app/contexts/RootStoreContext';
import currenciesMap from './assets/currencies_list.json';
import { Currency } from 'shared/components/CurrencySelect/types';
import {
  getCurrenciesArray,
  isShowingReportsView,
  isViewSupportsCurrencyFeatureCurrentPath,
} from 'shared/components/CurrencySelect/Currency.utils';

const currenciesArr = Object.values(currenciesMap);

export interface CurrencyContext {
  active: boolean;
  viewNotSupported: boolean;
  providerCurrency: string;
  currenciesArray: Currency[];
  currencyInUse: Currency | null;
  currenciesMap: Record<string, Currency>;
  isProcessingCurrencyChangedByAdmin?: string | null;
  isLoading?: boolean;
  exchangeRate?: number;
  sessionCurrencyCode?: string | null;
  processingCurrency?: string | null;
  showDisclaimer: boolean;
  isReportsView: boolean;
  processingCurrencyUpdateTime?: string | null;
}
//eslint-disable-next-line no-redeclare
const CurrencyContext = createContext<CurrencyContext | null>(null);

export const useCurrencyContext = (): CurrencyContext => {
  const context = useContext<CurrencyContext | null>(CurrencyContext);
  return context ?? ({} as CurrencyContext);
};

export const withCurrencyContextProvider = <P extends object>(Component: ComponentType<P>) => {
  const CurrencyContextProvider = (props: Readonly<P>) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [localAccountId, setLocalAccountId] = useState<string | null>(null);
    const location = window.location.pathname;

    const { usersStore } = useRootStore();

    const prevViewNotSupportedRef = useRef<boolean | null>(null);
    const accountId = usersStore?.accountInfo?.account_id ?? usersStore?.getCurrDisplayedAccountId();
    const localMainAccount = usersStore?.getMainUser?.()?.accounts?.find((x) => x.accountId === accountId);

    const [exchangeRate, setExchangeRate] = useState<number>(1);

    const getExchangeRateFromAPI = async (currencyFrom: string, currencyTo: string): Promise<void> => {
      const response = await usersStore.getAccountLatestCurrency(localMainAccount?.rateType, currencyFrom, currencyTo);
      setExchangeRate(response?.exchangeRate);
      setIsLoading(false);
    };

    const processingCurrency = useMemo(() => {
      return localMainAccount?.processingCurrency ?? 'USD';
    }, [localMainAccount?.processingCurrency]);

    const providerCurrency = useMemo(() => {
      return localMainAccount?.providerCurrency ?? 'USD';
    }, [localMainAccount?.providerCurrency]);

    useEffect(() => {
      if (accountId && accountId !== localAccountId) {
        window.sessionStorage.removeItem(`processingCurrencyCode-${localAccountId}`);
        setLocalAccountId(accountId);
      }
      if (currenciesMap[processingCurrency]) {
        window.sessionStorage.setItem(`processingCurrencyCode-${accountId}`, processingCurrency);
      }
    }, [accountId, localAccountId, processingCurrency]);

    const [currencyValue, setCurrencyValue] = useState({
      viewNotSupported: true,
      active: false,
      sessionCurrencyCode: null,
      currenciesMap,
      currenciesArray: [] as Currency[],
      processingCurrency,
      providerCurrency,
      isProcessingCurrencyChangedByAdmin: usersStore?.adminProcessingCurrency,
      currencyInUse: null,
      exchangeRate,
      isLoading,
      showDisclaimer: true,
      isReportsView: false,
      processingCurrencyUpdateTime: localMainAccount?.processingCurrencyUpdateTime,
    });

    const featureFlagOn = checkFeatureFlag(usersStore, ACCOUNT_FEATURES.USE_CURRENCIES);

    useEffect(() => {
      let currency: Currency | null = currencyValue.currencyInUse as Currency | null;
      if (currency && currency?.code !== providerCurrency) {
        if (currency?.code !== localMainAccount?.processingCurrency) {
          setIsLoading(true);
          getExchangeRateFromAPI(providerCurrency, currency?.code);
        }
      }
      //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currencyValue.currencyInUse, providerCurrency, localMainAccount?.processingCurrency]);

    useEffect(() => {
      const viewSupportsCurrencyFeature = isViewSupportsCurrencyFeatureCurrentPath(location);
      const featureIsActiveButViewNotSupported = featureFlagOn && !viewSupportsCurrencyFeature;
      const featureIsActiveNoCurrencySession = featureFlagOn && !usersStore.sessionCurrencyCode;
      const featureIsActiveWithCurrencySession = !!featureFlagOn && !!usersStore.sessionCurrencyCode;
      let currency: Currency = currenciesMap['USD'];

      const isLocationShowingReportsView = isShowingReportsView(location);
      let isReportsView = false;

      switch (true) {
        case isLocationShowingReportsView: {
          currency = currenciesMap[processingCurrency];
          usersStore.updateCurrencyContextActive(false);
          isReportsView = currency?.code !== providerCurrency;
          break;
        }
        case !featureFlagOn || featureIsActiveButViewNotSupported: {
          currency = currenciesMap[providerCurrency];
          usersStore.updateCurrencyContextActive(false);
          break;
        }
        case featureIsActiveNoCurrencySession: {
          currency = currenciesMap[processingCurrency];
          usersStore.updateCurrencyContextActive(true);
          break;
        }
        case featureIsActiveWithCurrencySession: {
          currency = currenciesMap[usersStore.sessionCurrencyCode ?? 'USD'];
          usersStore.updateCurrencyContextActive(true);
          break;
        }
        default: {
          usersStore.updateCurrencyContextActive(false);
          break;
        }
      }
      let localShowDisclaimer = false;
      if (
        prevViewNotSupportedRef.current === true &&
        !featureIsActiveButViewNotSupported &&
        currency?.code !== providerCurrency
      ) {
        localShowDisclaimer = true;
      }
      prevViewNotSupportedRef.current = featureIsActiveButViewNotSupported;

      const currenciesArray: Currency[] = getCurrenciesArray(
        usersStore.sessionCurrencyCode,
        processingCurrency,
        providerCurrency,
        currenciesArr as Currency[],
      );

      let localExchangeRate = exchangeRate;
      if (currency?.code !== providerCurrency) {
        if (currency?.code === localMainAccount?.processingCurrency) {
          localExchangeRate = Number(localMainAccount?.exchangeRate);
        }
      }

      setCurrencyValue({
        viewNotSupported: featureIsActiveButViewNotSupported,
        active: featureFlagOn,
        sessionCurrencyCode: usersStore.sessionCurrencyCode,
        currenciesMap,
        currenciesArray,
        processingCurrency,
        providerCurrency,
        isProcessingCurrencyChangedByAdmin: usersStore?.adminProcessingCurrency,
        currencyInUse: currency as unknown as null,
        exchangeRate: localExchangeRate,
        isLoading,
        showDisclaimer: localShowDisclaimer,
        isReportsView,
        processingCurrencyUpdateTime: localMainAccount?.processingCurrencyUpdateTime,
      });
      //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      usersStore.sessionCurrencyCode,
      providerCurrency,
      processingCurrency,
      featureFlagOn,
      location,
      usersStore.adminProcessingCurrency,
      isLoading,
      exchangeRate,
      localMainAccount?.exchangeRate,
      localMainAccount?.processingCurrency,
      localMainAccount?.processingCurrencyUpdateTime,
    ]);

    useEffect(() => {
      if (!currenciesMap[usersStore.sessionCurrencyCode]) {
        usersStore.updateSessionCurrencyCode();
      }
      if (!currenciesMap[usersStore.adminProcessingCurrency]) {
        usersStore.updateAdminProcessingCurrency();
      }
      //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usersStore.sessionCurrencyCode, usersStore.adminProcessingCurrency, localMainAccount?.processingCurrency]);

    useEffect(() => {
      const handleSessionCurrencyUpdate = () => {
        const currentSessionCurrency = window.sessionStorage.getItem('sessionCurrencyCode');
        const adminCurrency = window.sessionStorage.getItem('adminProcessingCurrency');

        if (
          currentSessionCurrency &&
          currentSessionCurrency !== usersStore.sessionCurrencyCode &&
          currenciesMap[currentSessionCurrency]
        ) {
          usersStore.updateAdminProcessingCurrency(adminCurrency);
          usersStore.updateSessionCurrencyCode(currentSessionCurrency);

          window.location.reload();
        }
      };

      window.addEventListener('sessionCurrencyUpdated', handleSessionCurrencyUpdate);

      return () => {
        window.removeEventListener('sessionCurrencyUpdated', handleSessionCurrencyUpdate);
      };
      //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usersStore.sessionCurrencyCode, usersStore.adminProcessingCurrency]);

    return (
      <CurrencyContext.Provider value={currencyValue}>
        <Component {...props} />
      </CurrencyContext.Provider>
    );
  };

  return observer(CurrencyContextProvider);
};

export function withCurrencyContextConsumer(Component) {
  const CurrencyContextConsumer = (props) => {
    const currencyContext = useCurrencyContext();
    return <Component {...props} currencyContext={currencyContext} />;
  };

  return observer(CurrencyContextConsumer);
}
